/**
 * A booking attempt the API was too down to accept, reshaped as a lead (SP-400).
 * Deliberately not a booking: confirming a time needs the API that just failed,
 * so replaying one later would promise an appointment nobody scheduled. The
 * intent survives instead, on the contact-form spine every booking already emits.
 */

import type { FieldValue } from "../../forms/form-data";

/** One field answer as prose; a structured address carries its own display string. */
const fieldValueText = (value: FieldValue): string => {
  if (value === undefined || value === null) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (typeof value === "object") {
    return value.formatted ?? "";
  }
  return String(value);
};
const clean = (value: string | null | undefined): string => {
  if (typeof value === "string") {
    return value.trim();
  }
  return "";
},
  /** One readable block, because the owner reads a message and not a JSON bag. */
  composeMessage = (input: ParkedBookingInput): string => {
    const lines: string[] = [],
      service = clean(input.service),
      preferredTime = clean(input.preferredTime);

    if (service) {
      lines.push(`Service: ${service}`);
    }
    if (preferredTime) {
      lines.push(`Preferred time: ${preferredTime}`);
    }
    for (const detail of input.details ?? []) {
      const value = clean(detail.value);
      if (value) {
        lines.push(`${clean(detail.label) || "Answer"}: ${value}`);
      }
    }

    const note = clean(input.note);
    if (note) {
      lines.push(...(lines.length ? ["", note] : [note]));
    }

    return lines.join("\n");
  },
  parkedBookingPayload = (input: ParkedBookingInput): Record<string, unknown> => {
    const details = (input.details ?? []).filter((d) => clean(d.value)),
      service = clean(input.service),
      preferredTime = clean(input.preferredTime),
      name = clean(input.name),
      email = clean(input.email);

    return {
      phone: clean(input.phone),
      ...(name ? { name } : {}),
      ...(email ? { email } : {}),
      message: composeMessage(input),
      ...(service ? { service } : {}),
      ...(preferredTime ? { preferredTime } : {}),
      ...(details.length ? { bookingDetails: details } : {}),
      formType: "booking",
      sessionId: input.sessionId ?? undefined,
      source: "booking_widget",
    };
  };

interface ParkedBookingDetail {
  label: string;
  value: string;
}

interface ParkedBookingInput {
  phone: string;
  name?: string;
  email?: string;
  service?: string | null;
  /** Free text: what the visitor said, not a slot anything can be booked against. */
  preferredTime?: string;
  note?: string;
  /** Form-builder answers, labels already resolved against the booking form. */
  details?: ParkedBookingDetail[];
  sessionId?: string | null;
}

export type { ParkedBookingDetail, ParkedBookingInput };
export { fieldValueText, parkedBookingPayload };
